import { useCallback } from 'react';
import billingApi from '@/api/billing';
import { useAppDispatch, useAppSelector } from '@/hooks/types';
import { getStripeCustomerId } from '@/store/user/selectors';
import { setProducts, setProductsError, setProductsLoading } from '@/store/billing/slice';
import { addNotification } from '@/store/app/slice';
import { NotificationMessageType, NotificationType } from '@/types/app';
import { PaymentIntentMetadata } from '@/types/billing';

interface StripeHook {
    getStripeProducts: () => Promise<void>;
    createPaymentIntent: (priceId: string) => Promise<{ id: string; clientSecret: string; amount: number; } | null>;
    validatePromoCode: (promoCode: string, paymentIntentId: string) => Promise<{ promoCode: string; newAmount: number; percent_off: number; originalAmount: number; discount: number; } | null>;
    updatePaymentIntent: (paymentIntentId: string, amount: number, metadata?: PaymentIntentMetadata) => Promise<{ id: string; clientSecret: string; amount: number; } | null>;
}

export const useStripe = (): StripeHook => {
    const dispatch = useAppDispatch();
    const stripeCustomerId = useAppSelector(getStripeCustomerId) ?? "";

    const createPaymentIntent = useCallback(async (priceId: string) => {
        try {
            const intent = await billingApi.createPaymentIntent({ priceId, stripeCustomerId });
            return { id: intent.id, clientSecret: intent.clientSecret, amount: intent.amount };
        } catch (error) {
            console.error('Failed to create payment intent:', error);
            return null;
        }
    }, [stripeCustomerId]);

    const getStripeProducts = useCallback(async () => {
        try {
            dispatch(setProductsLoading());
            const products = await billingApi.getStripeProducts();
            dispatch(setProducts(products));
        } catch (error) {
            console.error('Failed to get Stripe products:', error);
            dispatch(setProductsError(error as string));
        }
    }, [dispatch]);

    const validatePromoCode = useCallback(async (code: string, paymentIntentId: string) => {
        try {
            const { promoCode, newAmount, percent_off, originalAmount, discount } = await billingApi.validatePromoCode({ promoCode: code, paymentIntentId });
            if (promoCode) {
                dispatch(
                    addNotification({
                        messageType: NotificationMessageType.PROMO_CODE_APPLIED,
                        title: `Code ${code} applied successfully`,
                        type: NotificationType.SUCCESS,
                    }),
                );
            } else {
                dispatch(
                    addNotification({
                        messageType: NotificationMessageType.PROMO_CODE_INVALID,
                        title: `Code ${code} is invalid`,
                        type: NotificationType.ERROR,
                    }),
                );
            }
            return { promoCode, newAmount, percent_off, originalAmount, discount };
        } catch (error) {
            dispatch(
                addNotification({
                    messageType: NotificationMessageType.PROMO_CODE_INVALID,
                    title: `Code ${code} is invalid`,
                    type: NotificationType.ERROR,
                }),
            );
            return null;
        }
    }, [dispatch]);

    const updatePaymentIntent = useCallback(async (paymentIntentId: string, amount: number, metadata?: PaymentIntentMetadata) => {
        try {
            const intent = await billingApi.updatePaymentIntent({ paymentIntentId, amount, metadata });
            return { id: intent.id, clientSecret: intent.clientSecret, amount: intent.amount };
        } catch (error) {
            console.error('Failed to update payment intent:', error);
            return null;
        }
    }, []);

    return { getStripeProducts, createPaymentIntent, validatePromoCode, updatePaymentIntent };
};